import React, { useCallback, useMemo } from 'react'

import { useReactFlow } from '@xyflow/react'
import { cloneDeep } from 'lodash'

import Box from 'components_new/atoms/Box'

import Node, { InputType } from 'components_new/molecules/Node'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import FunctionSelect from 'components_new/molecules/Node/FunctionSelect'

import NodeType from 'types/transformation/enums/NodeType'
import DataType from 'types/transformation/enums/DataType'
import {
  CastNode,
  Node as TNode
} from 'types/transformation/types/nodes'
import { DATA_TYPES } from 'helpers/Constants'

import { pruneRef, useInputSchema, useIsInvalid, usePruneRefs, useColumnOrigins } from '../hooks'
import { getChangeColumn } from './utils'

interface CastNodeProps {
  id: string
  data: CastNode
  selected: boolean
}

const TYPE_OPTIONS = Object.values(DataType).map((dtype) => ({
  id: dtype,
  name: DATA_TYPES[dtype]
}))

const Cast = (props: CastNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  // Which source table each Input node reads, so a column can show its origin
  // table name (not just the node number) in the field selector.
  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      inputSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [inputSchema, originByNode]
  )

  // input schema has changed, validate references
  usePruneRefs<CastNode>(
    id,
    (nodeData) => {
      const pruned = nodeData.change_column
        ? !pruneRef(nodeData.change_column.id, inputSchema)
        : true

      return {
        ...nodeData,
        change_column: pruned ? null : nodeData.change_column
      }
    },
    [inputSchema]
  )

  const handleSelectColumn = useCallback(
    (leftColumn: string | null) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) return node

          const column = leftColumn
            ? inputSchema.find((item) => item.id === leftColumn)
            : null

          const newNodeData = cloneDeep(node.data) as CastNode

          newNodeData.change_column = column
            ? getChangeColumn(newNodeData, column)
            : null

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id, inputSchema]
  )

  const handleSelectDataType = useCallback(
    (dtype: DataType) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as CastNode

          if (!newNodeData.change_column) {
            // do nothing
            return node
          }

          newNodeData.change_column.type = dtype

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="TransformOutlined"
      inputType={InputType.SINGLE}
      title={'Cast'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.CAST}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          width: '272px'
        }}
      >
        <FieldSelect
          value={data.change_column?.id ?? null}
          options={fieldOptions}
          onChange={handleSelectColumn}
          placeholder="Välj attribut"
        />

        {data.change_column ? (
          <FunctionSelect
            value={data.change_column.type}
            options={TYPE_OPTIONS}
            onChange={(value) => handleSelectDataType(value as DataType)}
            sx={{ width: '100%' }}
          />
        ) : null}
      </Box>
    </Node>
  )
}

export default Cast
